/**
 * Display formatting. Pure string/integer work — no `Intl` number coercion of a
 * money field, no `parseFloat`, no `Number()` on anything that came off the
 * wire as a decimal string (docs/04 §2.3).
 *
 * Display tolerates a last-digit error; the ledger does not, and the ledger is
 * the server's. That asymmetry is why rounding lives here and nowhere else.
 *
 * Every directional value carries a glyph and an explicit sign. Colour is never
 * the only signal (design-system §4, CVD modes).
 */

import { BPS_DP, USDC_DP, SHARES_DP, formatFixed, parseFixed, type Outcome } from './decimal'

export const UP_GLYPH = '▲'
export const DOWN_GLYPH = '▼'

/** The threshold at which the countdown gains a decimal place — and the point
 *  at which D9 requires 10 Hz rendering. One number, two meanings, deliberately. */
export const COUNTDOWN_DECIMAL_THRESHOLD_MS = 10_000

// ---------------------------------------------------------------------------
// The countdown
// ---------------------------------------------------------------------------

/**
 * `M:SS` is dead weight when the minute digit is always `0`
 * (design-system §2.1). Bare seconds down to 10, then one decimal for the final
 * ten — the decimal is the signal that the market has entered its last phase,
 * and it arrives before the user has to read a colour.
 *
 * Never returns a negative: past expiry the server's state wins and
 * `<ResolutionPanel>` takes the slot.
 */
export function formatCountdown(remainingMs: number): string {
  const ms = Math.max(0, remainingMs)
  if (ms >= COUNTDOWN_DECIMAL_THRESHOLD_MS) return String(Math.floor(ms / 1000))
  // Floor, not round: showing `10.0` while 9.96s remain reads as a stalled clock.
  return (Math.floor(ms / 100) / 10).toFixed(1)
}

/**
 * `21:46:10` — a wall-clock instant, **in UTC**.
 *
 * UTC because the rest of the product already is: a `market_id` is an epoch
 * second, the strip's title reads `… — 21:46 UTC`, and a settlement window
 * labelled in the reader's local zone would name a different time than the
 * market it settles, on the one surface whose whole job is to be checkable
 * against the venue (`components.md` §2.6, §2.8).
 *
 * Not `toISOString().slice(11, 19)`, which allocates a 24-character string and
 * a `Date` to return eight characters, sixty times an hour.
 */
export function formatUtcTime(atMs: number): string {
  const total = Math.floor(atMs / 1000)
  const seconds = ((total % 60) + 60) % 60
  const minutes = ((Math.floor(total / 60) % 60) + 60) % 60
  const hours = ((Math.floor(total / 3600) % 24) + 24) % 24
  return `${pad2(hours)}:${pad2(minutes)}:${pad2(seconds)}`
}

function pad2(value: number): string {
  return String(value).padStart(2, '0')
}

/** `mm:ss`, for the minutes-away case only — a `SCHEDULED` market up to
 *  `n_ahead` periods out. Inside a live market, use `formatCountdown`. */
export function formatMmSs(remainingMs: number): string {
  const total = Math.floor(Math.max(0, remainingMs) / 1000)
  const minutes = Math.floor(total / 60)
  return `${minutes}:${String(total % 60).padStart(2, '0')}`
}

// ---------------------------------------------------------------------------
// Prices
// ---------------------------------------------------------------------------

/** `63¢`. Prices are integer cents; there is nothing to round. */
export function formatCents(priceCents: number): string {
  return `${priceCents}¢`
}

/**
 * A 4dp decimal string **in cents** → `57¢`, `57.25¢`.
 *
 * `avg_fill_price_cents` is a decimal string and not an integer for the reason
 * §7.1 gives: an average of integers is not an integer. It is therefore not
 * `formatCents`'s input, and running it through `Number()` to make it one would
 * put a float on a price — which invariant 6 bans and which `parseFixed` makes
 * unnecessary.
 */
export function formatAvgCents(value: string): string {
  return `${trimZeros(formatFixed(parseFixed(value, 4), 4))}¢`
}

/**
 * An observation's own decimal string as a money label: `"63441.28419"` →
 * `$63,441.28`, `"+23.2200"` → `+$23.22`.
 *
 * **Sliced, never parsed.** These strings come off the oracle at 8 *or* 18
 * decimals depending on the stream (`docs/05`, invariant 11), so there is no
 * scale to hand `parseFixed`, and `Number()` on a 22-digit mantissa loses both
 * ends of it. Display tolerates a last-digit error and the ledger does not — and
 * the ledger is the server's, which is the whole reason this file rounds and
 * nothing downstream of it does.
 *
 * The sign is preserved where the input carries one, because every directional
 * value in this product shows an explicit sign and never relies on colour.
 */
export function formatPriceString(value: string, dp = 2): string {
  const signed = value.startsWith('+') || value.startsWith('-')
  const sign = signed ? (value[0] as string) : ''
  const [whole = '0', frac = ''] = (signed ? value.slice(1) : value).split('.')
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return dp === 0 ? `${sign}$${grouped}` : `${sign}$${grouped}.${frac.slice(0, dp).padEnd(dp, '0')}`
}

/**
 * `cost_basis / shares` as a 4dp cents string, for the receipt's `@ 0.63`.
 *
 * Exact integer work on two 6dp fields — `notional_µ × 100 × 10⁴ / shares_µ` is
 * cents × 10⁴ — which is the same arithmetic the engine uses for
 * `avg_fill_price_cents` and the reason that field is a decimal string rather
 * than an integer in the first place (§7.1: an average of integers is not an
 * integer). No float touches it.
 */
export function avgEntryCents(costBasis: string, shares: string): string | null {
  const cost = parseFixed(costBasis, USDC_DP)
  const size = parseFixed(shares, SHARES_DP)
  if (size <= 0n) return null
  return formatFixed((cost * 100n * 10_000n) / size, 4)
}

/** A price rendered as the probability it is: `63¢` → `63%`. */
export function formatCentsAsPercent(priceCents: number): string {
  return `${priceCents}%`
}

/**
 * `(100 − p) / p` as a signed percentage — the "Return" line of
 * `<PayoutPreview>` (design-system §2.4). Integer math to tenths; a buy at 63¢
 * returns `+58.7%`.
 */
export function formatReturnPct(priceCents: number): string {
  if (priceCents <= 0) return '—'
  const tenths = roundedDiv(BigInt(100 - priceCents) * 1000n, BigInt(priceCents))
  return `${signOf(tenths)}${formatFixed(absOf(tenths), 1)}%`
}

/** A 4dp probability string (`"0.6300"`) as a percentage: `63%`. */
export function formatProbability(probability: string): string {
  const scaled = parseFixed(probability, 4) // 0.6300 -> 6300
  const percentTenths = roundedDiv(scaled * 10n, 100n)
  const whole = percentTenths / 10n
  const remainder = percentTenths % 10n
  return remainder === 0n ? `${whole}%` : `${formatFixed(percentTenths, 1)}%`
}

// ---------------------------------------------------------------------------
// Money and shares
// ---------------------------------------------------------------------------

/**
 * `"114.800000"` → `$114.80`. Two decimal places for display, taken from the
 * exact 6dp value with round-half-up — the sixth decimal is still on the wire
 * and still in the ledger.
 */
export function formatUsdc(value: string, options: { dp?: number } = {}): string {
  const dp = options.dp ?? 2
  const micro = parseFixed(value, USDC_DP)
  const rounded = roundedDiv(absOf(micro), 10n ** BigInt(USDC_DP - dp))
  return `${micro < 0n ? '-' : ''}$${groupThousands(formatFixed(rounded, dp))}`
}

/** Always signed, for P&L and deltas: `+$23.12`, `-$4.00`. */
export function formatSignedUsdc(value: string, options: { dp?: number } = {}): string {
  const micro = parseFixed(value, USDC_DP)
  const magnitude = formatUsdc(formatFixed(absOf(micro), USDC_DP), options)
  return `${signOf(micro)}${magnitude}`
}

/** Compact volume for the market strip: `$86K`, `$1.2M`. Display only. */
export function formatUsdcCompact(value: string): string {
  const micro = parseFixed(value, USDC_DP)
  return `${micro < 0n ? '-' : ''}$${compactWhole(absOf(micro) / 10n ** BigInt(USDC_DP))}`
}

/** Compact share count for the leaderboard: `86K`, `1.2M`. Display only. */
export function formatSharesCompact(value: string): string {
  const micro = parseFixed(value, SHARES_DP)
  return `${micro < 0n ? '-' : ''}${compactWhole(absOf(micro) / 10n ** BigInt(SHARES_DP))}`
}

function compactWhole(whole: bigint): string {
  if (whole >= 1_000_000n) return `${trimZeros(formatFixed(roundedDiv(whole, 100_000n), 1))}M`
  if (whole >= 1_000n) return `${trimZeros(formatFixed(roundedDiv(whole, 100n), 1))}K`
  return String(whole)
}

/**
 * `"100.000000"` → `100`; `"12.500000"` → `12.5`; `"100000000.000000"` →
 * `100,000,000`. Trailing zeros are noise.
 *
 * **Grouped, like every other money label in this file.** It was the one that
 * was not, and the gap only shows at size: `100000000` is nine digits a reader
 * has to count, in a right-aligned column next to `$100,000,000.00` that
 * already groups. Nothing about the value changes — the whole path here is
 * `bigint`, so the ceiling is the column's width and never the arithmetic.
 */
export function formatShares(value: string): string {
  return groupThousands(trimZeros(formatFixed(parseFixed(value, SHARES_DP), SHARES_DP)))
}

/** `"+4.0400"` → `+4.04 bps`. Basis points are 4dp on the wire. */
export function formatBps(value: string): string {
  const raw = parseFixed(value, BPS_DP)
  return `${signOf(raw)}${trimZeros(formatFixed(absOf(raw), BPS_DP))} bps`
}

// ---------------------------------------------------------------------------
// Direction
// ---------------------------------------------------------------------------

export function glyphFor(outcome: Outcome): string {
  return outcome === 'UP' ? UP_GLYPH : DOWN_GLYPH
}

/** `▲ UP`, `▼ DOWN` — the glyph and the word, never the colour alone. */
export function formatOutcome(outcome: Outcome): string {
  return `${glyphFor(outcome)} ${outcome}`
}

/**
 * A signed decimal with its direction glyph: `▲ +25.62250000`. `dp` is the
 * field's own scale, so a strike delta keeps every digit the oracle published.
 */
export function formatSignedDelta(value: string, dp: number): string {
  const raw = parseFixed(value, dp)
  const glyph = raw < 0n ? DOWN_GLYPH : UP_GLYPH
  return `${glyph} ${signOf(raw)}${formatFixed(absOf(raw), dp)}`
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

function absOf(v: bigint): bigint {
  return v < 0n ? -v : v
}

function signOf(v: bigint): string {
  return v < 0n ? '-' : '+'
}

/** Round-half-away-from-zero on a non-negative integer division. */
function roundedDiv(numerator: bigint, denominator: bigint): bigint {
  const negative = numerator < 0n
  const n = negative ? -numerator : numerator
  const q = n / denominator
  const r = n % denominator
  const rounded = r * 2n >= denominator ? q + 1n : q
  return negative ? -rounded : rounded
}

function trimZeros(decimal: string): string {
  if (!decimal.includes('.')) return decimal
  return decimal.replace(/\.?0+$/, '')
}

function groupThousands(decimal: string): string {
  const [whole = '0', frac] = decimal.split('.')
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return frac === undefined ? grouped : `${grouped}.${frac}`
}

/**
 * `7 days`, `24 hours`, `90 minutes` — a session key's lifetime, whatever
 * `config.session_keys.key_ttl_ms` actually says. Here rather than beside the
 * arming modal because the ticket's gate (`domain/`, no React) says it too.
 */
export function formatTtl(ttlMs: number): string {
  const minutes = Math.round(ttlMs / 60_000)
  if (minutes % 1440 === 0) {
    const days = minutes / 1440
    return `${days} ${days === 1 ? 'day' : 'days'}`
  }
  if (minutes % 60 === 0) {
    const hours = minutes / 60
    return `${hours} ${hours === 1 ? 'hour' : 'hours'}`
  }
  return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`
}
